import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { Box, Stack, xcss } from '@atlaskit/primitives';
import { useDatabaseContent } from '@databases/database-state';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { DatabaseFieldType } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/utils/database/changesets';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';

import CalendarGrid from './CalendarGrid';
import MonthSwitcher from './MonthSwitcher';

export interface EntryCardProps {
  contentId: string;
  entryId: string;
  fieldId: string;
  isReadonly?: boolean;
}

const Calendar = ({
  EntryCardValueComponent,
  canEditContent,
}: {
  canEditContent: boolean;
  EntryCardValueComponent: React.ComponentType<EntryCardProps>;
}) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const { contentId } = useDatabaseContent();
  const [month, setMonth] = useState(dayjs().month());
  const [year, setYear] = useState(dayjs().year());

  const handleMonthChange = (newMonth: number, newYear: number) => {
    setMonth(newMonth);
    setYear(newYear);
  };

  useEffect(() => {
    return monitorForElements({
      onDrop: ({ source, location }) => {
        const destination = location.current.dropTargets[0];
        if (!destination || !canEditContent) {
          return;
        }

        const locationData = destination.data as unknown as {
          day?: dayjs.Dayjs;
        };

        const sourceData = source.data as unknown as {
          fieldId: string;
          entryId: string;
        };

        if (!destination.data.day) {
          return;
        }

        dispatch(
          addChangeToDraft(contentId as string, 'main', {
            type: DatabaseChangeType.SET_VALUE,
            fieldId: sourceData.fieldId,
            entryId: sourceData.entryId,
            value: {
              type: DatabaseFieldType.DATE,
              value: locationData.day?.format('YYYY-MM-DD'),
            },
            change: {
              type: DatabaseFieldType.DATE,
              value: locationData.day?.format('YYYY-MM-DD'),
            },
          })
        );
      },
    });
  }, [contentId, dispatch, canEditContent]);

  return (
    <Box padding="space.200">
      <Stack space="space.200" xcss={xcss({ position: 'relative' })}>
        <MonthSwitcher month={month} year={year} onMonthChange={handleMonthChange} />
        <CalendarGrid
          month={month}
          year={year}
          canEditContent={canEditContent}
          EntryCardValueComponent={EntryCardValueComponent}
        />
      </Stack>
    </Box>
  );
};

export default Calendar;
